import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faXmark,
} from "@fortawesome/sharp-regular-svg-icons";
import classNames from "classnames";
import { getAppealCurrentStatusLabel } from "shared/src/constants/appeal-current-status";

import styles from "./status-dropdown.module.scss";

interface StatusDropdownProps {
  selectedStatuses: string[];
  onStatusChange: (statuses: string[]) => void;
  availableStatuses: readonly string[];
}

export const StatusDropdown = ({
  selectedStatuses,
  onStatusChange,
  availableStatuses,
}: StatusDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleStatusSelect = (status: string) => {
    const newStatuses = selectedStatuses.includes(status)
      ? selectedStatuses.filter((s) => s !== status)
      : [...selectedStatuses, status];

    onStatusChange(newStatuses);
  };

  const handleRemoveStatus = (
    status: string,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    const newStatuses = selectedStatuses.filter((s) => s !== status);
    onStatusChange(newStatuses);
  };

  return (
    <div className={styles.statusDropdown} ref={dropdownRef}>
      <div
        role="button"
        tabIndex={0}
        className={styles.dropdownButton}
        onClick={handleToggleDropdown}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggleDropdown();
          }
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className={styles.buttonContent}>
          {selectedStatuses.length === 0 ? (
            <span className={styles.placeholder}>Appeal Status</span>
          ) : (
            <div className={styles.selectedPills}>
              {selectedStatuses.map((status) => {
                const label = getAppealCurrentStatusLabel(status);
                return (
                  <span
                    key={status}
                    className={classNames(
                      styles.selectedPill,
                      styles[`pill${label.replace(/\s/g, "")}`],
                    )}
                  >
                    {label}
                    <button
                      type="button"
                      className={styles.removePillButton}
                      onClick={(e) => handleRemoveStatus(status, e)}
                      aria-label={`Remove ${label} filter`}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          <FontAwesomeIcon
            icon={isOpen ? faChevronUp : faChevronDown}
            className={styles.chevronIcon}
          />
        </div>
      </div>
      {isOpen && (
        <div className={styles.dropdownMenu} role="listbox">
          {availableStatuses.map((status) => {
            const isSelected = selectedStatuses.includes(status);
            const label = getAppealCurrentStatusLabel(status);

            return (
              <button
                key={status}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={classNames(
                  styles.dropdownOption,
                  styles[`pill${label.replace(/\s/g, "")}`],
                  {
                    [styles.selected]: isSelected,
                  },
                )}
                onClick={() => handleStatusSelect(status)}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
